import type { BookingProposalRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';

/**
 * Canonical primary garage for a stationary proposal's service department — same
 * contract as the booking flow's garage choice. Shared by the suggested times on
 * the landing card and the full calendar picker so both hold the same bay pool.
 */
export const useProposalStationaryGarage = (proposal: BookingProposalRecord | undefined) => {
  const { currentBrand } = useCurrentBrand();

  const serviceDepartmentId = proposal?.serviceDepartment.id;
  const brandDomain = proposal?.serviceDepartment.brand.domain ?? currentBrand;

  const {
    data: stationaryGarages,
    isLoading,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getStationaryGarages,
    input: {
      brandDomain,
      withCapacity: false
    },
    queryConfig: { enabled: serviceDepartmentId != null && !!brandDomain }
  });

  const garageId =
    serviceDepartmentId != null
      ? stationaryGarages?.find((candidate) => candidate.serviceDepartmentId === serviceDepartmentId)?.garageId
      : undefined;

  const salesItems = proposal?.bookingProposalItems[0]?.salesItemsWithQuantity ?? [];
  const primarySalesItemId = salesItems.find((si) => !si.salesItem.isAddon)?.salesItem.id;
  const addonSalesItemIds = salesItems.filter((si) => si.salesItem.isAddon).map((si) => si.salesItem.id);
  const salesItemIds = primarySalesItemId != null ? [primarySalesItemId, ...addonSalesItemIds] : undefined;

  return {
    brandDomain,
    garageId,
    salesItemIds,
    primarySalesItemId,
    addonSalesItemIds,
    /** True while the garage lookup has not yet produced an id. */
    isLoading: serviceDepartmentId != null && isLoading && garageId == null,
    /** The lookup finished and no garage matched — the flow cannot continue. */
    isMissingGarage: serviceDepartmentId != null && !isLoading && garageId == null,
    error
  };
};
